import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { User, isSuperUser } from "@/types/user";
import { useAuth } from "@/hooks/useAuth";
import { UserIcon } from "lucide-react";

interface SuperUserEmployerSelectorProps {
  selectedUserId: string;
  onUserChange: (userId: string) => void;
}

const SuperUserEmployerSelector: React.FC<SuperUserEmployerSelectorProps> = ({
  selectedUserId,
  onUserChange,
}) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isSuperUserState, setIsSuperUserState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isSuperUser(user)) {
      fetchUsers();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await ApiService.request<{ users: User[] }>(
        EndpointKeys.FETCH_ALL_USERS
      );
      setUsers(response.users);
      setIsSuperUserState(true);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setIsSuperUserState(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !isSuperUserState) {
    return null;
  }

  const isFirstVisit = selectedUserId === "current";

  return (
    <div className={`w-full md:w-64 ${isFirstVisit ? 'animate-pulse' : ''}`}>
      <div className="flex items-center mb-1">
        <UserIcon className="w-4 h-4 mr-2 text-blue-500" />
        <label className="text-sm font-medium text-gray-700">View as Employer</label>
      </div>
      <Select
        value={selectedUserId}
        onValueChange={onUserChange}
      >
        <SelectTrigger className={isFirstVisit ? "border-blue-500 ring-2 ring-blue-200" : ""}>
          <SelectValue placeholder="⚠️ Select an employer to view" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="current" className="text-foreground">Current User (You)</SelectItem>
          {users.map((user) => (
            <SelectItem key={user.id} value={user.id.toString()} className="text-foreground">
              {user.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isFirstVisit && (
        <p className="mt-1 text-xs text-blue-600">
          ⚠️ Please select an employer to proceed
        </p>
      )}
    </div>
  );
};

export default SuperUserEmployerSelector; 